import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';

import {
  FINCHGPT_EVENTS,
  FINCHGPT_PROPERTIES,
  getWindowDimensions,
} from '@/constants/analytics/finchgpt-events';
import { usePostHogCapture } from '@/hooks/usePostHogCapture';
import { AgentChatThread as TAgentChatThread } from '@/services/types/agent-chat-types';

import {
  useCreateAgentChatThreadMutation,
  useDeleteAgentChatThreadMutation,
  useListAgentChatThreadsQuery,
  useSuggestThreadNameMutation,
  useUpdateAgentChatThreadMutation,
} from '../../services/api/agentChatService';
import { AgentChatThread } from './AgentChatThread';
import { useAgentChat } from './context/AgentChatContext';
import { CreateThreadDialog } from './threads/CreateThreadDialog';
import { ThreadList } from './threads/ThreadList';
import { ThreadListTabs } from './threads/ThreadListTabs';
import { EmptyState } from './ui/EmptyState';

interface AgentChatProps {
  matterId: string;
  workspaceId?: string;
  viewMode?: 'compact' | 'fullscreen';
  onToolCall?: (toolName: string, input: unknown, output?: unknown) => void;
  initialThreadId?: string | null;
  onDeeplinkConsumed?: () => void;
  children: ReactNode;
}

interface AgentChatCompoundContextType {
  threads: TAgentChatThread[];
  isLoadingThreads: boolean;
  selectedThreadId: string | null;
  matterId: string;
  workspaceId?: string;
  viewMode: 'compact' | 'fullscreen';
  handleSelectThread: (threadId: string | null) => void;
  handleCreateThread: (
    title: string,
    initialTask: string | null,
  ) => Promise<void>;
  handleRenameThread: (threadId: string, newTitle: string) => Promise<void>;
  handleDeleteThread: (threadId: string) => Promise<void>;
  handleOpenCreateThreadDialog: () => void;
  handleCloseCreateThreadDialog: () => void;
  handleCreateThreadDirect: () => Promise<void>;
  handleAutoRenameThread: (threadId: string) => Promise<void>;
  handleCopyThreadLink: (threadId: string) => void;
  isNewThreadDialogOpen: boolean;
  onToolCall?: (toolName: string, input: unknown, output?: unknown) => void;
}

const isDefaultNewThreadTitle = (title: string): boolean =>
  /^New Thread(?:\s*-\s*.*)?$/.test(title);

const AgentChatCompoundContext =
  createContext<AgentChatCompoundContextType | null>(null);

export const useAgentChatCompound = () => {
  const context = useContext(AgentChatCompoundContext);
  if (!context) {
    throw new Error(
      'useAgentChatCompound must be used within an AgentChat component',
    );
  }
  return context;
};

export const AgentChat = ({
  matterId,
  workspaceId,
  viewMode = 'compact',
  onToolCall,
  initialThreadId: initialThreadIdProp,
  onDeeplinkConsumed,
  children,
}: AgentChatProps) => {
  const capture = usePostHogCapture();
  const {
    data: threads = [],
    isLoading: isLoadingThreads,
    isFetching: isFetchingThreads,
  } = useListAgentChatThreadsQuery({ matterId, workspaceId });
  const { selectedThreadId, setSelectedThreadId, setMobileView } =
    useAgentChat();
  const [isNewThreadDialogOpen, setIsNewThreadDialogOpen] = useState(false);
  const [createThread] = useCreateAgentChatThreadMutation();
  const [updateThread] = useUpdateAgentChatThreadMutation();
  const [deleteThread] = useDeleteAgentChatThreadMutation();
  const [suggestThreadName] = useSuggestThreadNameMutation();

  const handleCreateThread = useCallback(
    async (title: string, initialTask: string | null) => {
      if (title.trim()) {
        const thread = await createThread({
          matterId,
          data: { title, initial_task: initialTask, workspace_id: workspaceId },
        }).unwrap();
        setSelectedThreadId(thread.id);
        setIsNewThreadDialogOpen(false);

        capture(FINCHGPT_EVENTS.THREAD_CREATED, {
          [FINCHGPT_PROPERTIES.MATTER_ID]: matterId,
          [FINCHGPT_PROPERTIES.THREAD_ID]: thread.id,
          [FINCHGPT_PROPERTIES.WORKSPACE_ID]: workspaceId,
          [FINCHGPT_PROPERTIES.VIEW_MODE]: viewMode,
          [FINCHGPT_PROPERTIES.THREAD_COUNT]: threads.length + 1,
          ...getWindowDimensions(),
        });
      }
    },
    [
      matterId,
      workspaceId,
      createThread,
      setSelectedThreadId,
      setIsNewThreadDialogOpen,
      capture,
      viewMode,
      threads.length,
    ],
  );

  // Track whether the deeplink initial thread has been applied
  const initialThreadAppliedRef = useRef(false);

  useEffect(() => {
    if (isFetchingThreads || isLoadingThreads) return;

    // Handle deeplink initial thread selection
    if (initialThreadIdProp && !initialThreadAppliedRef.current) {
      initialThreadAppliedRef.current = true;
      onDeeplinkConsumed?.();
      const targetThread = threads.find((t) => t.id === initialThreadIdProp);
      if (targetThread) {
        setSelectedThreadId(targetThread.id);
        return;
      }
      // Thread not found — it may have been deleted
      toast.error('This conversation could not be found. It may have been deleted.');
      // Fallback to the latest available thread
      if (threads.length > 0) {
        setSelectedThreadId(threads[threads.length - 1].id);
      }
      return;
    }

    if (threads.length > 0 && !selectedThreadId) {
      const latestThread = threads[threads.length - 1];
      setSelectedThreadId(latestThread.id);
    }

    if (!threads.find((thread) => thread.id === selectedThreadId)) {
      setSelectedThreadId(null);
    }
  }, [
    threads,
    selectedThreadId,
    setSelectedThreadId,
    isFetchingThreads,
    isLoadingThreads,
    initialThreadIdProp,
    onDeeplinkConsumed,
  ]);

  const handleRenameThread = useCallback(
    async (threadId: string, newTitle: string) => {
      if (newTitle.trim()) {
        try {
          await updateThread({
            matterId,
            threadId,
            data: { title: newTitle },
          }).unwrap();

          capture(FINCHGPT_EVENTS.THREAD_RENAMED, {
            [FINCHGPT_PROPERTIES.MATTER_ID]: matterId,
            [FINCHGPT_PROPERTIES.THREAD_ID]: threadId,
            [FINCHGPT_PROPERTIES.VIEW_MODE]: viewMode,
          });
        } catch (error) {
          toast.error('Failed to rename thread');
        }
      }
    },
    [updateThread, matterId, capture, viewMode],
  );

  const handleDeleteThread = useCallback(
    async (threadId: string) => {
      try {
        await deleteThread({
          matterId,
          threadId,
        }).unwrap();

        capture(FINCHGPT_EVENTS.THREAD_DELETED, {
          [FINCHGPT_PROPERTIES.MATTER_ID]: matterId,
          [FINCHGPT_PROPERTIES.THREAD_ID]: threadId,
          [FINCHGPT_PROPERTIES.VIEW_MODE]: viewMode,
          [FINCHGPT_PROPERTIES.THREAD_COUNT]: threads.length - 1,
        });

        // If the deleted thread was selected, clear the selection
        if (selectedThreadId === threadId) {
          setSelectedThreadId(null);
        }
      } catch (error) {
        toast.error('Failed to delete thread');
      }
    },
    [matterId, selectedThreadId, setSelectedThreadId, deleteThread, capture, viewMode, threads.length],
  );

  const handleSelectThread = useCallback(
    (threadId: string | null) => {
      const isMobile = window.innerWidth < 768;

      // On mobile, if clicking the already selected thread, switch to chat view
      if (isMobile && threadId === selectedThreadId && threadId !== null) {
        setMobileView('chat');
      } else {
        setSelectedThreadId(threadId);

        if (threadId) {
          capture(FINCHGPT_EVENTS.THREAD_SELECTED, {
            [FINCHGPT_PROPERTIES.MATTER_ID]: matterId,
            [FINCHGPT_PROPERTIES.THREAD_ID]: threadId,
            [FINCHGPT_PROPERTIES.VIEW_MODE]: viewMode,
            ...getWindowDimensions(),
          });
        }
      }
    },
    [setSelectedThreadId, selectedThreadId, setMobileView, capture, matterId, viewMode],
  );

  const handleOpenCreateThreadDialog = () => {
    setIsNewThreadDialogOpen(true);
  };

  const handleCloseCreateThreadDialog = () => {
    setIsNewThreadDialogOpen(false);
  };

  // Direct thread creation without dialog - uses default name
  const handleCreateThreadDirect = useCallback(async () => {
    const timestamp = new Date().toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    const defaultTitle = `New Thread - ${timestamp}`;

    try {
      await handleCreateThread(defaultTitle, null);
    } catch (error) {
      // Error handling is already in handleCreateThread
    }
  }, [handleCreateThread]);

  // Auto-suggest a better name for a thread (can be called after messages are added)
  const handleAutoRenameThread = useCallback(
    async (threadId: string) => {
      try {
        const result = await suggestThreadName({
          matterId,
          threadId,
        }).unwrap();

        if (result.suggested_name && result.suggested_name !== 'New Thread') {
          // Only update if the suggested name is meaningful
          const thread = threads.find((t) => t.id === threadId);
          // Only auto-rename if the current name looks like a default name
          if (thread && isDefaultNewThreadTitle(thread.title)) {
            await updateThread({
              matterId,
              threadId,
              data: { title: result.suggested_name },
            }).unwrap();
          }
        }
      } catch (error) {
        // Silent fail - this is an optional enhancement
        console.error('Failed to auto-rename thread:', error);
      }
    },
    [matterId, suggestThreadName, threads, updateThread],
  );

  const handleCopyThreadLink = useCallback(
    (threadId: string) => {
      const url = new URL(window.location.href);
      // Clean existing params and set deeplink params
      url.search = '';
      url.searchParams.set('finchgpt', threadId);
      url.searchParams.set('chat_version', 'agent');
      navigator.clipboard.writeText(url.toString()).then(() => {
        toast.success('Link copied to clipboard');
      }).catch(() => {
        toast.error('Failed to copy link');
      });
    },
    [],
  );

  const contextValue = useMemo(
    () => ({
      threads,
      isLoadingThreads,
      selectedThreadId,
      matterId,
      workspaceId,
      viewMode,
      handleSelectThread,
      handleCreateThread,
      handleRenameThread,
      handleDeleteThread,
      handleOpenCreateThreadDialog,
      handleCloseCreateThreadDialog,
      handleCreateThreadDirect,
      handleAutoRenameThread,
      handleCopyThreadLink,
      isNewThreadDialogOpen,
      onToolCall,
    }),
    [
      threads,
      isLoadingThreads,
      selectedThreadId,
      matterId,
      workspaceId,
      viewMode,
      handleSelectThread,
      handleCreateThread,
      handleRenameThread,
      handleDeleteThread,
      handleCreateThreadDirect,
      handleAutoRenameThread,
      handleCopyThreadLink,
      isNewThreadDialogOpen,
      onToolCall,
    ],
  );

  return (
    <AgentChatCompoundContext.Provider value={contextValue}>
      <div className="h-full w-full flex">{children}</div>
      <CreateThreadDialog
        isOpen={isNewThreadDialogOpen}
        onClose={handleCloseCreateThreadDialog}
        onCreateThread={handleCreateThread}
      />
    </AgentChatCompoundContext.Provider>
  );
};

// Compound components
const ThreadListComponent = () => {
  const {
    threads,
    isLoadingThreads,
    selectedThreadId,
    handleSelectThread,
    handleCreateThreadDirect,
    handleDeleteThread,
    handleRenameThread,
    handleCopyThreadLink,
  } = useAgentChatCompound();

  return (
    <ThreadList
      threads={threads}
      selectedThreadId={selectedThreadId}
      isLoading={isLoadingThreads}
      onSelectThread={handleSelectThread}
      onCreateThread={handleCreateThreadDirect}
      onDeleteThread={handleDeleteThread}
      onRenameThread={handleRenameThread}
      onCopyLink={handleCopyThreadLink}
    />
  );
};

const ThreadListTabsComponent = () => {
  const {
    threads,
    isLoadingThreads,
    selectedThreadId,
    handleSelectThread,
    handleCreateThreadDirect,
    handleDeleteThread,
    handleRenameThread,
  } = useAgentChatCompound();

  return (
    <ThreadListTabs
      threads={threads}
      selectedThreadId={selectedThreadId}
      onSelectThread={handleSelectThread}
      onCreateThread={handleCreateThreadDirect}
      onDeleteThread={handleDeleteThread}
      onRenameThread={handleRenameThread}
      isLoading={isLoadingThreads}
    />
  );
};

const ContentComponent = () => {
  const {
    selectedThreadId,
    handleCreateThreadDirect,
    handleAutoRenameThread,
    matterId,
    workspaceId,
    viewMode,
    threads,
    onToolCall,
  } = useAgentChatCompound();

  // Check if this is a thread with no messages yet (for auto-rename)
  const currentThread = threads.find((t) => t.id === selectedThreadId);
  const shouldAutoRename =
    currentThread &&
    (currentThread.title.startsWith('New Thread') ||
      currentThread.title.includes(' - '));

  return (
    <div className="flex-1 h-full">
      {selectedThreadId ? (
        <AgentChatThread
          matterId={matterId}
          threadId={selectedThreadId}
          workspaceId={workspaceId}
          viewMode={viewMode}
          onToolCall={onToolCall}
          onFirstMessage={
            shouldAutoRename
              ? () => handleAutoRenameThread(selectedThreadId)
              : undefined
          }
        />
      ) : (
        <EmptyState onCreateThread={handleCreateThreadDirect} />
      )}
    </div>
  );
};

// Attach compound components to the main component
AgentChat.ThreadList = ThreadListComponent;
AgentChat.ThreadListTabs = ThreadListTabsComponent;
AgentChat.Content = ContentComponent;
